import React, { useState, useEffect, useContext, useRef } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment-timezone';
import TextField from '@material-ui/core/TextField';
import { DATE_TIME_PICKER_SUGGEST_BOX_WIDTH_PX } from '@mummssoftware/common-ui';
import { emptyText, error } from '@mummssoftware/utils/constants';
import { formatAdmitPostDate } from '@mummssoftware/utils/formatters';
import { GlobalContext } from '@mummssoftware/global-context/GlobalContext';
import { offsetDate, toSiteTz } from '@mummssoftware/utils/orders/orders-utils';
import {
  OrdersGlobalContext,
  SET_ORDER_STATEMENT,
} from '@mummssoftware/global-context/OrdersGlobalContext';
import {
  FOR_CAUSE,
  LEFT_GEOGRAPHIC_AREA,
  NON_CERTIFIED,
  DEATH,
  REVOKED,
  TRANSFERRED,
} from '@mummssoftware/utils/orders/orders-constants';
import { DischargePicker, EffDateTimePicker } from '../Components/components';
import {
  SET_EFFECTIVE_DATE,
  SET_ORDER_DISABLED,
  SET_DISCHARGE_REASON,
} from '../Context/constants';
import { ProgressNotesContext } from '../Context/ProgressNotesContext';
import { useStyles } from '../style/style';
import { Text } from '../../components/layout/WrapperDiv';
import { Context } from '../../context';
import defaults from '../../translations/en.json';
import { getFormattedDisMsg, getDischargeReason, isDisType } from './utils';

type DischargeStatementProps = {
  isorder: boolean;
};

const DischargeStatement = ({ isorder }: DischargeStatementProps) => {
  const intl = useIntl();
  const classes = useStyles();

  const [dischargeReason, setDischargeReason] = useState<
    mumms.OptionType | undefined
  >(undefined);

  const {
    dispatch,
    dischargeTypes,
    dischargeMapping,
    orderDisabled,
    orderCreated,
    approvalDateError,
    handleSendApproval,
    isPhysicianRoleGroup,
  } = useContext(ProgressNotesContext);
  const {
    orderType,
    admitPhysician,
    verbalDate,
    createOrder,
    dispatchOrders,
    orderStatement,
  } = useContext(OrdersGlobalContext);
  const {
    dischargePatient,
    savedApproval,
    patient,
    agency,
    currentUser,
  } = useContext(Context);
  const { patientDateRestrictions } = useContext(GlobalContext);

  const tz = moment.tz.guess();
  const siteTimezone = (currentUser && currentUser.siteTimezone) || tz;

  const [date, setDate] = useState<Date | null>(null);
  const siteDate = date && toSiteTz(date, siteTimezone);

  const isDischargeType = isDisType(orderType);

  // on Mount
  useEffect(() => {
    // reset the order disabled to default value
    dispatch({
      type: SET_ORDER_DISABLED,
      payload: true,
    });
  }, []);

  const [errorDate, setErrorDate] = useState<boolean>(false);

  const handleDateChange = (dischargedate: any) => {
    const newDate = offsetDate(dischargedate);
    const newMoment = moment(newDate);
    setDate(newDate);
    dispatch({
      type: SET_EFFECTIVE_DATE,
      payload: newDate,
    });
    // minimumDate={minDischargeDate.toDate()}
    // maximumDate={Date.now()}
    const isInvalidDate = !newMoment.isValid();
    const maxDateError = newMoment.diff(moment()) > 0;
    const minDateError = newMoment.diff(minDischargeDate) < 0;
    if (maxDateError || minDateError || isInvalidDate) {
      setErrorDate(true);
    } else {
      setErrorDate(false);
    }
  };

  const [dischageTextDone, setDischargeText] = useState<boolean>(false);
  const [hospiceText, setHospiceText] = useState<string>(emptyText);

  // Discharge is triggered from two useEffects (isorder vs !isorder). Deps such as
  // orderStatement and savedApproval can update in quick succession, so each effect
  // delays the API call with setTimeout(..., 400). clearTimeout in the effect cleanup
  // cancels a pending callback when deps change before the delay elapses.
  //
  // clearTimeout alone is not enough: if a callback already ran, or both effects
  // scheduled one, a later dep update could still fire dischargePatient again.
  // dischargeTriggeredRef is a fire-once guard for the current createOrder workflow.
  // It is reset when createOrder goes false so a new submission can discharge again.
  const dischargeTriggeredRef = useRef(false);

  useEffect(() => {
    if (!createOrder) {
      dischargeTriggeredRef.current = false;
    }
  }, [createOrder]);

  // get the DIS statement
  const getTextContent = () => {
    const dischargePayload = getFormattedDisMsg(
      intl,
      currentUser,
      orderType as mumms.DischargeEntityType,
      isorder,
      isPhysicianRoleGroup,
      date,
      dischargeReason,
      hospiceText,
    );

    dispatchOrders({
      type: SET_ORDER_STATEMENT,
      payload: dischargePayload,
    });
    setDischargeText(true);
  };

  // trigger get the DIS statement
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    if (createOrder && isDischargeType && !orderCreated) {
      // setTimeout as we want the approval statement to go first
      timeoutId = setTimeout(() => {
        getFirstWorkflow();
      }, 100);
    }
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrder]);

  const getFirstWorkflow = () => {
    getTextContent();

    if (isPhysicianRoleGroup) {
      handleSendApproval();
    }
  };

  // after Approval call is successful (savedApproval) trigger DIS API call
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    if (createOrder && !orderCreated) {
      if (isorder && dischageTextDone && !dischargeTriggeredRef.current) {
        timeoutId = setTimeout(() => {
          if (dischargeTriggeredRef.current) {
            return;
          }
          dischargeTriggeredRef.current = true;
          // setTimeout to make sure we get the correct orderStatement
          handleDischargePatient();
          setDischargeText(false);
        }, 400);
      }
    }
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrder, savedApproval, orderStatement, dischageTextDone]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    if (createOrder && !orderCreated) {
      const approvalReady = !isPhysicianRoleGroup || savedApproval;
      if (
        !isorder &&
        orderStatement &&
        approvalReady &&
        !dischargeTriggeredRef.current
      ) {
        timeoutId = setTimeout(() => {
          if (dischargeTriggeredRef.current) {
            return;
          }
          dischargeTriggeredRef.current = true;
          // setTimeout to make sure we get the correct orderStatement
          handleDischargePatient();
        }, 400);
      }
    }
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrder, savedApproval, orderStatement]);

  const handleDischargeChange = (disreason: any) => {
    setDischargeReason(disreason);
    dispatch({
      type: SET_DISCHARGE_REASON,
      payload: disreason,
    });
  };

  const handleDischargePatient = () => {
    // map the long name
    const dischargeData = {
      patientId: patient.id,
      patientnumber: patient.patientNumber,
      firstname: patient.firstName,
      lastname: patient.lastName,
      dischargeReasonString: getDischargeReason(
        orderType as mumms.DischargeEntityType,
        patient,
        dischargeReason,
        dischargeTypes,
      ),
      dischargeCodeString: dischargeMapping[orderType as string], // Left Geographic Area
      dischargeCodeType: orderType, // LEFT_GEOGRAPHIC_AREA
      dischargeDate: date && formatAdmitPostDate(date),
      billingRemark: '',
      createDischargeClaim: true,
      dischargeConfirmed: true,
    };

    orderStatement &&
      dischargePatient(agency, dischargeData, orderStatement, isorder);
  };

  const lastLOCDate = moment(patientDateRestrictions?.lastloc).startOf('day');
  const startVerbalDate = moment(verbalDate).startOf('day');

  // business rule: discharge date should be greater than the verbal date
  const minDischargeDate = verbalDate ? startVerbalDate : lastLOCDate;
  // there is an error in the approval process

  const [disOrderError, setDisOrderError] = useState<boolean>(true);

  // set order not disabled when checks have passed
  useEffect(() => {
    // there is an error in the approval process
    const approvalError = approvalDateError || !admitPhysician;
    const approvalErrorNeeded =
      isorder && !isPhysicianRoleGroup ? approvalError : false;
    // error in the common discharge process
    const dischargeError = !date || errorDate;

    switch (orderType) {
      case DEATH:
        {
          const orderError = approvalErrorNeeded || dischargeError;
          setDisOrderError(orderError);
        }
        break;
      case TRANSFERRED:
        {
          const transferOrderError = isorder
            ? !hospiceText || hospiceText === emptyText
            : false;
          const orderError =
            approvalErrorNeeded || dischargeError || transferOrderError;
          setDisOrderError(orderError);
        }
        break;
      case REVOKED:
        {
          const orderError =
            approvalErrorNeeded || dischargeError || !dischargeReason;
          setDisOrderError(orderError);
        }
        break;
      case FOR_CAUSE:
        {
          const orderError =
            approvalErrorNeeded || dischargeError || !dischargeReason;
          setDisOrderError(orderError);
        }
        break;
      default: {
        const orderError = approvalErrorNeeded || dischargeError;
        setDisOrderError(orderError);
      }
    }
  }, [
    approvalDateError,
    admitPhysician,
    date,
    errorDate,
    dischargeReason,
    hospiceText,
  ]);

  // set order not disabled when checks have passed
  useEffect(() => {
    if (!disOrderError && orderDisabled) {
      dispatch({
        type: SET_ORDER_DISABLED,
        payload: false,
      });
    }
    if (disOrderError && !orderDisabled) {
      dispatch({
        type: SET_ORDER_DISABLED,
        payload: true,
      });
    }
  }, [disOrderError, orderDisabled]);

  const handleHospiceChange = (event: any) => {
    const { value: nextValue } = event.target;
    setHospiceText(nextValue);
  };

  const getHospice = () => (
    <TextField
      id="hospice-input"
      variant="outlined"
      value={hospiceText}
      onChange={handleHospiceChange}
      InputProps={{
        classes: {
          input: classes.generalOrderInput,
          root: classes.generalOrderRoot,
        },
      }}
      classes={{
        root: classes.widthGeneralOrder,
      }}
    />
  );

  const getDischargeInfo = () => (
    <>
      <Text>{defaults.disStatement}</Text>
      {getDischargeDate()}
    </>
  );

  const getDischargeDate = () => (
    <EffDateTimePicker
      selectedDate={siteDate}
      minimumDate={minDischargeDate.toDate()} // MINIMUM OF VERBAL_DATE
      maximumDate={new Date()}
      handleEffDateChange={handleDateChange}
      width={DATE_TIME_PICKER_SUGGEST_BOX_WIDTH_PX}
      severity={!date || errorDate ? error : null}
    />
  );

  const DischargeReason = () => (
    <DischargePicker
      value={dischargeReason}
      options={
        orderType && dischargeTypes ? (dischargeTypes as any)[orderType] : []
      }
      handleDischargeChange={handleDischargeChange}
      severity={dischargeReason ? null : error}
    />
  );

  const getForCauseStatement = (
    disorder: boolean,
    isPhysicianRolegroup: boolean,
  ) => {
    if (disorder) {
      if (isPhysicianRolegroup) {
        return (
          <>
            <Text>{defaults.forCausePhysOrderText}</Text>
            <DischargeReason />
            {getDischargeInfo()}
          </>
        );
      }
      return (
        <>
          <Text>{defaults.disAcknowledge}</Text>
          <Text>{defaults.forCauseOrderText}</Text>
          <DischargeReason />
          {getDischargeInfo()}
        </>
      );
    }
    return (
      <>
        <Text>{defaults.forCauseNoteText}</Text>
        <DischargeReason />
        {getDischargeInfo()}
      </>
    );
  };

  const getGeographicStatement = (
    disorder: boolean,
    isPhysicianRolegroup: boolean,
  ) => {
    if (disorder) {
      if (isPhysicianRolegroup) {
        return (
          <>
            <Text>{defaults.geographicPhysOrderText}</Text>
            {getDischargeDate()}
          </>
        );
      }
      return (
        <>
          <Text>{defaults.disAcknowledge}</Text>
          <Text>{defaults.geographicOrderText}</Text>
          {getDischargeInfo()}
        </>
      );
    }
    return (
      <>
        <Text>{defaults.geographicNoteText}</Text>
        {getDischargeInfo()}
      </>
    );
  };

  const getNonCertifiedStatement = (
    disorder: boolean,
    isPhysicianRolegroup: boolean,
  ) => {
    if (disorder) {
      if (isPhysicianRolegroup) {
        return (
          <>
            <Text>{defaults.nonCertifiedPhysOrderText}</Text>
            {getDischargeDate()}
          </>
        );
      }
      return (
        <>
          <Text>{defaults.disAcknowledge}</Text>
          <Text>{defaults.nonCertifiedOrderText}</Text>
          {getDischargeInfo()}
        </>
      );
    }
    return (
      <>
        <Text>{defaults.nonCertNoteText}</Text>
        {getDischargeInfo()}
      </>
    );
  };

  const getDeathStatement = (disorder: boolean) => {
    if (disorder) {
      return (
        <>
          <Text>{defaults.disAcknowledge}</Text>
          <Text>{defaults.deathOrderText}</Text>
          {getDischargeDate()}
        </>
      );
    }
    return (
      <>
        <Text>{defaults.deathNoteText}</Text>
        <Text>{defaults.disDeathOrder}</Text>
        {getDischargeDate()}
      </>
    );
  };

  const getRevokedStatement = (disorder: boolean) => {
    if (disorder) {
      return (
        <>
          <Text>{defaults.disAcknowledge}</Text>
          <Text>{defaults.revokedOrderText}</Text>
          <DischargeReason />
          {getDischargeInfo()}
        </>
      );
    }
    return (
      <>
        <Text>{defaults.revokedNoteText}</Text>
        <DischargeReason />
        {getDischargeInfo()}
      </>
    );
  };

  const getTransferStatement = (disorder: boolean) => {
    if (disorder) {
      return (
        <>
          <Text>{defaults.disAcknowledge}</Text>
          <Text>{defaults.transferOrderText}</Text>
          {getHospice()}
          {getDischargeInfo()}
        </>
      );
    }
    return (
      <>
        <Text>{defaults.transferNoteText}</Text>
        {getDischargeInfo()}
      </>
    );
  };

  const getStatementUI = (
    dischargetype: mumms.DischargeEntityType | null,
    disorder: boolean,
  ) => {
    switch (dischargetype) {
      case FOR_CAUSE:
        return getForCauseStatement(disorder, isPhysicianRoleGroup);
      case LEFT_GEOGRAPHIC_AREA:
        return getGeographicStatement(disorder, isPhysicianRoleGroup);
      case NON_CERTIFIED:
        return getNonCertifiedStatement(disorder, isPhysicianRoleGroup);
      case DEATH:
        return getDeathStatement(disorder);
      case REVOKED:
        return getRevokedStatement(disorder);
      case TRANSFERRED: {
        return getTransferStatement(disorder);
      }
      default:
        return <Text>{defaults.disAcknowledge}</Text>;
    }
  };

  return getStatementUI(orderType as mumms.DischargeEntityType, isorder);
};

export default DischargeStatement;
